const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.mindfulme.pusatandalan.com';

export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image?: string;
  author_id?: string;
  author: {
    id?: string;
    name: string;
    email: string;
    photo?: string;
    bio?: string;
  };
  category: {
    id: string;
    name: string;
    slug: string;
  };
  tags: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  status: 'draft' | 'published' | 'archived';
  published_at?: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  read_time_minutes: number;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  author_name?: string;
  author_email?: string;
  author_phone?: string;
  type?: string;
  verification_status?: string;
  verification_notes?: string;
}

export interface ContentCategory {
  id: string;
  name: string;
  slug: string;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// API utility functions
// Helper to parse JSON with BOM handling
const parseJsonWithBOM = (text: string) => {
  // Remove BOM (Byte Order Mark) if present
  const cleanText = text.replace(/^\uFEFF/, '');
  return JSON.parse(cleanText);
};

const apiRequest = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const url = `${API_BASE_URL}/api${endpoint}`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  // Get response as text first to handle BOM
  const textData = await response.text();
  const data = parseJsonWithBOM(textData);

  return data.data || data;
};

// News API functions
export const getPublishedNews = async (params: {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  sort?: 'latest' | 'popular' | 'oldest' | 'title';
} = {}) => {
  const queryParams = new URLSearchParams();

  // Show all published categories by default, filter only when selected
  if (params.category && params.category !== 'all') {
    queryParams.append('category', params.category);
  }
  queryParams.append('status', 'published');

  // Add other parameters if they exist and are not undefined
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.search && params.search.trim() !== '' && params.search !== 'undefined') {
    queryParams.append('search', params.search.trim());
  }
  if (params.sort) queryParams.append('sort', params.sort);

  console.log('Fetching articles with params:', queryParams.toString());

  const response = await fetch(`${API_BASE_URL}/api/articles?${queryParams}`);
  if (!response.ok) {
    throw new Error('Failed to fetch news articles');
  }

  // Get response as text first to handle BOM (Byte Order Mark)
  const textData = await response.text();
  // Remove BOM if present
  const cleanText = textData.replace(/^\uFEFF/, '');
  // Parse cleaned JSON
  const data = JSON.parse(cleanText);

  console.log('Raw API Response:', data); // Debug log

  // API returns { success: true, data: [...], meta: {...} }
  // We want to return the data array
  if (data.data && Array.isArray(data.data)) {
    return data.data;
  } else if (Array.isArray(data)) {
    return data;
  } else {
    console.warn('Unexpected API response structure:', data);
    return [];
  }
};

export const getNewsArticleBySlug = async (slug: string): Promise<Article> => {
  const response = await fetch(`${API_BASE_URL}/api/articles/slug/${slug}`);
  if (!response.ok) {
    throw new Error('News article not found');
  }

  // Get response as text first to handle BOM
  const textData = await response.text();
  const cleanText = textData.replace(/^\uFEFF/, '');
  const data = JSON.parse(cleanText);

  return data.data || data;
};

export const trackNewsView = async (articleId: string) => {
  try {
    await fetch(`${API_BASE_URL}/api/articles/${articleId}/view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    // Silently fail for tracking
    console.warn('Failed to track news view:', error);
  }
};

// Volunteer registration API
export const registerVolunteer = async (data: {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
  address: string;
  motivation: string;
}) => {
  const response = await fetch(`${API_BASE_URL}/api/volunteers/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const textData = await response.text();
    const errorData = parseJsonWithBOM(textData);
    throw new Error(errorData.message || 'Failed to register volunteer');
  }

  const textData = await response.text();
  const result = parseJsonWithBOM(textData);
  return result.data || result;
};

// Volunteer login API
export const loginVolunteer = async (email: string, password: string) => {
  const response = await fetch(`${API_BASE_URL}/api/volunteers/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const textData = await response.text();
    const errorData = parseJsonWithBOM(textData);
    throw new Error(errorData.message || 'Login failed');
  }

  const textData = await response.text();
  const result = parseJsonWithBOM(textData);
  return result.data || result;
};

// Get volunteer profile
export const getVolunteerProfile = async (token: string) => {
  const response = await fetch(`${API_BASE_URL}/api/volunteers/profile`, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get volunteer profile');
  }

  const textData = await response.text();
  const result = parseJsonWithBOM(textData);
  return result.data || result;
};

// Get volunteer articles
export const getVolunteerArticles = async (token: string) => {
  const response = await fetch(`${API_BASE_URL}/api/articles/volunteer/my-articles`, {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get volunteer articles');
  }

  const textData = await response.text();
  const result = parseJsonWithBOM(textData);
  return result.data || result;
};

// Logout volunteer
export const logoutVolunteer = async (token: string) => {
  const response = await fetch(`${API_BASE_URL}/api/volunteers/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to logout');
  }

  return true;
};

export const searchNewsArticles = async (query: string) => {
  // Use the same articles endpoint with search parameter
  return await getPublishedNews({
    search: query,
  });
};

export const getArticleCategories = async (): Promise<ContentCategory[]> => {
  const response = await fetch(`${API_BASE_URL}/api/content/categories`);
  if (!response.ok) {
    throw new Error('Failed to fetch categories');
  }

  const textData = await response.text();
  const data = parseJsonWithBOM(textData);
  const categories = (data.data || data) as ContentCategory[];

  if (!Array.isArray(categories)) {
    return [];
  }

  return categories.filter((category) => category.is_active !== false);
};

// Helper function to get image URL
export const getImageUrl = (imagePath?: string) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;
  return `${API_BASE_URL}/storage/${imagePath}`;
};

// Helper function to format date
export const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('id-ID', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Helper function to format reading time
export const formatReadingTime = (minutes: number) => {
  if (minutes === 1) return '1 menit baca';
  if (minutes < 60) return `${minutes} menit baca`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}j ${remainingMinutes}m baca` : `${hours}j baca`;
};

// Types for volunteer submission
export interface ArticleSubmission {
  title: string;
  excerpt: string;
  content: string;
  author_name: string;
  author_email: string;
  author_phone: string;
  featured_image?: File;
  type?: 'volunteer_submission';
}

// Volunteer news submission API
export const submitNewsArticle = async (data: ArticleSubmission) => {
  const formData = new FormData();

  // Get token from localStorage
  const token = localStorage.getItem('volunteer_token');

  // Add all fields to FormData
  Object.keys(data).forEach(key => {
    const value = data[key as keyof ArticleSubmission];
    if (value !== undefined && value !== null) {
      if (key === 'featured_image' && value instanceof File) {
        formData.append('featured_image', value);
      } else if (key !== 'featured_image') {
        formData.append(key, value as string);
      }
    }
  });

  try {
    const response = await fetch(`${API_BASE_URL}/api/articles/volunteer-submit`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const textData = await response.text();
      const errorData = parseJsonWithBOM(textData);
      throw new Error(errorData.message || 'Failed to submit article');
    }

    const textData = await response.text();
    const result = parseJsonWithBOM(textData);
    return result.data || result;
  } catch (error: any) {
    console.error('Error submitting article:', error);
    throw error;
  }
};